import apiClient from './base/apiClient'
import { EarlierVisit } from '../types/review'
import { VisitType } from '../constants/visitType'
import { useReviewStore } from '../stores/reviewStore'

export interface CreateReviewRequest {
  placeId: number
  /**
   * 어떤 기록을 고칠지. 주면 수정, 안 주고 newVisit=true면 새 회차다.
   * 둘 다 없으면 서버가 옛 화면으로 보고 덮어쓴다(2026-09-21 이후 삭제 예정).
   */
  reviewId?: number
  newVisit?: boolean
  internalScore?: number // (구) 내부 계산 점수 (-2~12) — 존+순위 경로에선 미사용
  score?: number // (구) 클리핑된 최종 점수 (0~10) — 존+순위 경로에선 미사용
  zone?: number // K=3 존 (0=별로,1=보통,2=좋음)
  insertIndex?: number // 존 내 순위 삽입 위치 (이진탐색 결과)
  categoryId?: number // 본인이 선택한 카테고리 ID
  reviewText?: string // 한줄평 (nullable)
  visitDate?: string
  waitingTime?: string | null // 웨이팅 시간 ('none' 또는 분 단위. 121은 '2시간 이상', null: 없음)
  visitType: VisitType // 방문 타입 enum
  isPublic?: boolean // 공개 여부 (true: 공개, false: 나만보기)
  minPricePerPerson?: number | null // 최소 1인당 가격대
  maxPricePerPerson?: number | null // 최대 1인당 가격대
  photos?: Array<{
    id: string
    preview: string
    description?: string
  }>
  taggedFriendIds?: string[] // 친구 태그 목록
  reviewKeywordIds?: number[] // 리뷰 키워드 ID 목록
  /**
   * 이번에 치른 A/B 비교들. 점수 계산에는 쓰이지 않고 기록으로만 남는다
   * (점수는 insertIndex가 정한다). 비교가 잘 돌고 있는지 나중에 보려면 필요하다.
   */
  comparisonHistories?: Array<{
    currentPlaceId: number
    comparePlaceId: number
    isCurrentBetter: boolean
    comparePlaceScore: number | null
    comparisonLow: number
    comparisonHigh: number
    comparisonCount: number
  }>
}

export interface CreateReviewResponse {
  message: string
  review: {
    id: number
    place_id: number
    score: number
  }
}

/** 한 장소에 남긴 회차 하나 — 팝업·회차 선택 시트용 요약 */
export interface PlaceVisitSummary {
  id: number
  visit_number: number
  score: number | null
  visit_date: string | null
  is_public: boolean
}

export interface ReviewResponse {
  /** 이 장소에 내가 남긴 모든 회차 (최신순). 옛 서버는 안 보낼 수 있다 */
  visits?: PlaceVisitSummary[]
  /** 기록이 적어 점수를 가려야 하는 사용자인지 */
  hide_score?: boolean
  /** 한 장소에 남길 수 있는 기록 수. 서버가 정한다 */
  max_visits?: number
  review: {
    id: number
    visit_number?: number
    place_id: number
    review_category_id: number | null
    review_text: string | null // 한줄평 (nullable)
    visit_date: string | null
    waiting_time: number | null // 웨이팅 시간 (분 단위. 121 이상은 '2시간 이상', null: 없음)
    visit_type: VisitType // 방문 타입 enum
    is_public: boolean // 공개 여부 (true: 공개, false: 나만보기)
    internal_score: number | null
    score: number | null
    min_price_per_person: number | null // 최소 1인당 가격대
    max_price_per_person: number | null // 최대 1인당 가격대
    photos: Array<{
      id: string
      preview: string
      description?: string
      /**
       * 사진이 아직 안 그려졌을 때 대신 깔 아주 작은 판 (data URI, 1KB 남짓).
       * 아직 안 만들어진 사진은 없을 수 있다.
       */
      blur?: string | null
    }>
    category: {
      id: number
      name: string
    } | null
    comparison_histories: Array<{
      current_place_id: number
      compare_place_id: number
      is_current_better: boolean
      compare_place_score: number | null
      comparison_low: number
      comparison_high: number
      comparison_count: number
    }>
    tagged_friends: Array<{
      id: number
      username?: string
      nickname: string
      profile_image: string | null
    }>
    keywords: Array<{
      id: number
      name: string
    }>
  }
}

export interface ReviewListResponse {
  reviews: Array<{
    id: string
    user_id: string
    username: string | null
    nickname: string
    profile_image_url: string | null
    rating: number // 0~10 (DB score와 동일)
    images: string[]
    content: string
    like_count: number
    is_liked: boolean
    created_at: string | null
    visit_date?: string | null // 방문 날짜 (없으면 생성 날짜 사용)
    visit_type: VisitType // 방문 타입
    hide_score?: boolean
  }>
  average_rating: number
  review_count: number
}

export interface ToggleLikeResponse {
  is_liked: boolean
  like_count: number
}

export interface FeedReview {
  id: string
  placeId: number
  placeAddress?: string
  user: {
    nickname: string
    username: string | null
    profileImage: string | null
    userId: string | null
  }
  restaurant: {
    name: string
    rating: number // 0~10
    category: string
  }
  reviewText: string | null // 한줄평 (nullable)
  minPricePerPerson?: number | null
  maxPricePerPerson?: number | null
  taggedFriends?: Array<{
    id: number
    username?: string
    nickname: string
    profileImage?: string
  }>
  keywords?: Array<{ id: number; name: string }>
  images: string[]
  /** images와 같은 순서. 사진이 아직 안 그려진 순간에 대신 깔 작은 판 */
  imageBlurs?: (string | null)[]
  likes: number
  isLiked: boolean
  comments: number
  date: string
  dateDisplay?: string
  visitType: VisitType
  isPublic?: boolean
  visitNumber?: number
  earlierVisits?: EarlierVisit[]
  isFollowing: boolean
  isBookmarked: boolean
  hasUserReview: boolean
  hideScore?: boolean
}

export interface FeedReviewResponse {
  reviews: FeedReview[]
  current_page: number
  per_page: number
  /** 팔로잉 탭은 커서 방식이라 전체 개수를 세지 않는다 (불필요한 COUNT 제거) */
  total?: number
  last_page: number
  has_more: boolean
  /**
   * 이어받기 위치. 다음 요청에 그대로 돌려주면 이 지점 다음부터 온다.
   * OFFSET과 달리 도중에 새 글이 올라와도 이미 본 구간이 다시 오지 않는다.
   */
  next_cursor?: string | null
}

export interface SearchReviewResponse {
  reviews: Array<{
    id: string
    placeId: number | null
    placeAddress?: string
    user: {
      nickname: string
      username: string | null
      profileImage: string | null
      userId: string | null
    }
    restaurant: {
      name: string
      rating: number // 0~10
      category: string
    }
    reviewText: string | null // 한줄평 (nullable)
    minPricePerPerson?: number | null
    maxPricePerPerson?: number | null
    taggedFriends?: Array<{
      id: number
      username?: string
      nickname: string
      profileImage?: string
    }>
    keywords?: Array<{ id: number; name: string }>
    images: string[]
    likes: number
    isLiked: boolean
    comments: number
    date: string
    dateDisplay?: string
    visitType: VisitType
    isPublic?: boolean
    visitNumber?: number
    earlierVisits?: EarlierVisit[]
    isFollowing: boolean
    isBookmarked: boolean
    hasUserReview?: boolean
    hideScore?: boolean
  }>
  current_page?: number
  has_more?: boolean
}

/** 댓글에서 언급(@아이디)된 사용자 */
export interface CommentMentionResponse {
  username: string
  userId: string
  nickname: string
}

export interface ReviewCommentResponse {
  id: string
  user: {
    nickname: string
    username?: string | null
    profileImage: string | null
  }
  userId?: string | null
  text: string
  /** 스티커 댓글이면 그 id. 글 댓글이면 null. (constants/commentStickers) */
  stickerId?: string | null
  likes: number
  isLiked: boolean
  parentCommentId?: string
  /**
   * 본문의 "@아이디" 중 실제 언급으로 인정된 것만 담긴다.
   * 서버가 맞팔로우 여부까지 확인한 결과이므로, 화면은 이 목록에 있는 아이디만
   * 강조하고 프로필로 연결한다. (없는 아이디나 맞팔이 아닌 사람은 그냥 글자로 남는다)
   */
  mentions?: CommentMentionResponse[]
}

export interface GetCommentsResponse {
  comments: ReviewCommentResponse[]
}

export interface CreateCommentRequest {
  /** 스티커만 보낼 때는 비운다. 서버가 둘 중 하나는 있어야 한다고 본다. */
  commentText?: string
  stickerId?: string
  parentCommentId?: number
}

export interface CreateCommentResponse {
  message: string
  comment: ReviewCommentResponse
}

export const reviewApi = {
  createReview: async (
    data: CreateReviewRequest,
  ): Promise<CreateReviewResponse> => {
    const response = await apiClient.post<CreateReviewResponse>('/reviews', {
      placeId: data.placeId,
      reviewId: data.reviewId,
      newVisit: data.newVisit,
      internalScore: data.internalScore,
      score: data.score,
      zone: data.zone,
      insertIndex: data.insertIndex,
      categoryId: data.categoryId,
      reviewText: data.reviewText,
      visitDate: data.visitDate,
      waitingTime: data.waitingTime,
      visitType: data.visitType,
      isPublic: data.isPublic,
      minPricePerPerson: data.minPricePerPerson,
      maxPricePerPerson: data.maxPricePerPerson,
      photos: data.photos,
      taggedFriendIds: data.taggedFriendIds,
      reviewKeywordIds: data.reviewKeywordIds,
      comparisonHistories: data.comparisonHistories,
    })
    return response.data
  },
  getFeed: async (
    page: number = 1,
    filter: 'recommended' | 'following' = 'recommended',
    location?: { lat: number; lng: number } | null,
    /**
     * 이어받기 위치(팔로잉 탭). 있으면 이 지점 다음부터 받아온다.
     * page 기반 OFFSET은 도중에 새 글이 올라오면 목록이 밀려서
     * 같은 리뷰가 두 번 뜨기 때문에 2페이지부터는 커서를 쓴다.
     */
    cursor?: string | null,
    /**
     * 추천 피드에서 '다음 묶음'으로 넘길지.
     *
     * 사용자가 직접 당겨서 새로고침했을 때만 true를 보낸다. 화면 재진입이나
     * 탭 전환처럼 의도치 않은 1페이지 요청에서도 넘겨버리면, 오가는 것만으로
     * 안 본 기록이 10개씩 건너뛰어진다.
     */
    rotate?: boolean,
  ): Promise<FeedReviewResponse> => {
    const response = await apiClient.get<FeedReviewResponse>('/reviews/feed', {
      params: {
        page,
        filter,
        // 추천 피드 위치 근접 보정 (있을 때만; following엔 무시됨)
        ...(location ? { lat: location.lat, lng: location.lng } : {}),
        ...(cursor ? { cursor } : {}),
        ...(rotate ? { rotate: 1 } : {}),
      },
    })
    return response.data
  },
  search: async (keyword: string): Promise<SearchReviewResponse> => {
    const response = await apiClient.get<SearchReviewResponse>(
      '/reviews/search',
      {
        params: {
          keyword,
        },
      },
    )
    return response.data
  },
  getByCategory: async (): Promise<SearchReviewResponse> => {
    const response =
      await apiClient.get<SearchReviewResponse>('/reviews/category')
    return response.data
  },
  getByUserId: async (
    userId: string,
    categoryId?: string,
    type?: string,
    page: number = 1,
    all: boolean = false,
  ): Promise<SearchReviewResponse> => {
    if (!userId) {
      throw new Error('사용자 ID가 필요합니다.')
    }
    const params: Record<string, string | number | boolean> = { page }
    if (categoryId) {
      params.categoryId = categoryId
    }
    if (type) {
      params.type = type
    }
    if (all) {
      params.all = true
    }
    const response = await apiClient.get<SearchReviewResponse>(
      `/reviews/user/${userId}`,
      {
        params,
      },
    )
    return response.data
  },
  getExperienceByUserId: async (
    userId: string,
    categoryId?: string,
    page: number = 1,
    all: boolean = false,
    /** 카테고리 탭. 화면은 code('cafe')로 쥐고 있어 서버가 id로 바꿔 쓴다 */
    categoryCode?: string,
    /** 돋보기 검색어. 가게 이름과 기록할 때 고른 키워드를 본다 (리뷰 본문은 제외) */
    keyword?: string,
  ): Promise<SearchReviewResponse> => {
    if (!userId) {
      throw new Error('사용자 ID가 필요합니다.')
    }
    const params: Record<string, string | number | boolean> = { page }
    if (categoryId) {
      params.categoryId = categoryId
    }
    if (categoryCode) {
      params.categoryCode = categoryCode
    }
    if (keyword) {
      params.keyword = keyword
    }
    if (all) {
      params.all = true
    }
    const response = await apiClient.get<SearchReviewResponse>(
      `/reviews/user/${userId}/experience`,
      {
        params,
      },
    )
    return response.data
  },
  /**
   * 서버에 저장된 리뷰 사진을 base64로 받아온다.
   *
   * 브라우저가 직접 못 가져오기 때문이다 — CloudFront가 CORS 헤더를 주지 않아
   * 캔버스로 픽셀을 꺼낼 수 없다. 기존 사진을 다시 편집할 때만 쓴다.
   */
  /**
   * 사진 한 장을 미리 올리고 주소를 받아온다.
   *
   * 실패해도 화면을 막지 않는다 — null 을 돌려주면 저장할 때 예전처럼
   * 본문에 실어 보낸다(느리지만 동작은 한다).
   */
  uploadPhoto: async (dataUrl: string): Promise<string | null> => {
    try {
      const response = await apiClient.post<{ url: string }>(
        '/reviews/photo',
        { photo: dataUrl },
        { skipErrorHandler: true } as any,
      )
      return response.data?.url ?? null
    } catch {
      return null
    }
  },
  getPhotoData: async (url: string): Promise<string | null> => {
    try {
      const response = await apiClient.get<{ data: string }>(
        '/reviews/photo-data',
        { params: { url }, skipErrorHandler: true } as any,
      )
      return response.data?.data ?? null
    } catch {
      return null
    }
  },
  /**
   * 이 장소에 내가 남긴 기록을 가져온다.
   * reviewId를 주면 그 회차, 안 주면 가장 최근 회차다.
   */
  getReviewByPlace: async (
    placeId: number,
    reviewId?: number,
  ): Promise<ReviewResponse | null> => {
    try {
      const response = await apiClient.get<ReviewResponse>(
        `/reviews/place/${placeId}${reviewId ? `?reviewId=${reviewId}` : ''}`,
        {
          skipErrorHandler: true, // 404 에러는 리뷰가 없는 정상적인 상황이므로 에러 메시지 표시 안 함
        } as any,
      )
      return response.data
    } catch (error: any) {
      // 404 에러는 리뷰가 없는 정상적인 상황이므로 null 반환
      if (error.response?.status === 404) {
        return null
      }
      // 다른 에러는 그대로 throw
      throw error
    }
  },
  getReviewListByPlace: async (
    placeId: number,
  ): Promise<ReviewListResponse> => {
    const response = await apiClient.get<ReviewListResponse>(
      `/reviews/place/${placeId}/list`,
    )
    return response.data
  },
  getReview: async (reviewId: number): Promise<{ review: FeedReview }> => {
    const response = await apiClient.get<{ review: FeedReview }>(
      `/reviews/${reviewId}`,
    )
    return response.data
  },
  getReviewDetail: async (
    reviewId: number,
  ): Promise<{
    review: {
      id: number
      place_id: number
      place: {
        id: number
        name: string
        address: string | null
        category: string
      } | null
      user_id?: string | null
      username?: string | null
      nickname?: string | null
      score: number | null
      hide_score?: boolean
      /** 이 장소의 몇 번째 기록인지 (재방문). 옛 서버는 안 보낼 수 있다 */
      visit_number?: number
      visit_date: string | null
      created_at: string | null
      date_display?: string
      visit_type: VisitType
      review_text: string | null
      min_price_per_person: number | null
      max_price_per_person: number | null
      photos: Array<{
        id: string
        preview: string
        description?: string
        /** 사진이 아직 안 그려졌을 때 대신 깔 작은 판 (data URI) */
        blur?: string | null
      }>
      tagged_friends: Array<{
        id: number
        username?: string
        nickname: string
        profile_image?: string | null
      }>
      keywords: Array<{
        id: number
        name: string
      }>
      has_user_review: boolean
      is_bookmarked: boolean
      like_count: number
      is_liked: boolean
    }
  }> => {
    const response = await apiClient.get<{
      review: {
        id: number
        place_id: number
        place: {
          id: number
          name: string
          address: string | null
          category: string
        } | null
        user_id?: string | null
        username?: string | null
        nickname?: string | null
        score: number | null
        hide_score?: boolean
        visit_date: string | null
        created_at: string | null
        date_display?: string
        visit_type: VisitType
        review_text: string | null
        min_price_per_person: number | null
        max_price_per_person: number | null
        photos: Array<{
          id: string
          preview: string
          description?: string
          /** 사진이 아직 안 그려졌을 때 대신 깔 작은 판 (data URI) */
          blur?: string | null
        }>
        tagged_friends: Array<{
          id: number
          nickname: string
        }>
        keywords: Array<{
          id: number
          name: string
        }>
        has_user_review: boolean
        is_bookmarked: boolean
        like_count: number
        is_liked: boolean
      }
    }>(`/reviews/${reviewId}/detail`)
    return response.data
  },
  toggleLike: async (reviewId: number): Promise<ToggleLikeResponse> => {
    const response = await apiClient.post<ToggleLikeResponse>(
      `/reviews/${reviewId}/like`,
    )
    return response.data
  },
  report: async (
    reviewId: number,
    data: {
      reason: 'spam' | 'inappropriate' | 'harassment' | 'other'
      description: string | null
    },
  ): Promise<{ message: string }> => {
    const response = await apiClient.post<{ message: string }>(
      `/reviews/${reviewId}/report`,
      {
        reason: data.reason,
        description: data.description,
      },
    )
    return response.data
  },
  getComments: async (reviewId: number): Promise<GetCommentsResponse> => {
    const response = await apiClient.get<GetCommentsResponse>(
      `/reviews/${reviewId}/comments`,
    )
    return response.data
  },
  createComment: async (
    reviewId: number,
    data: CreateCommentRequest,
  ): Promise<CreateCommentResponse> => {
    const response = await apiClient.post<CreateCommentResponse>(
      `/reviews/${reviewId}/comments`,
      {
        commentText: data.commentText,
        stickerId: data.stickerId,
        parentCommentId: data.parentCommentId,
      },
    )
    return response.data
  },
  updateComment: async (
    reviewId: number,
    commentId: number,
    data: { commentText: string },
  ): Promise<{ message: string; comment: ReviewCommentResponse }> => {
    const response = await apiClient.put<{
      message: string
      comment: ReviewCommentResponse
    }>(`/reviews/${reviewId}/comments/${commentId}`, {
      commentText: data.commentText,
    })
    return response.data
  },
  toggleCommentLike: async (
    reviewId: number,
    commentId: number,
  ): Promise<ToggleLikeResponse> => {
    const response = await apiClient.post<ToggleLikeResponse>(
      `/reviews/${reviewId}/comments/${commentId}/like`,
    )
    return response.data
  },
  deleteComment: async (
    reviewId: number,
    commentId: number,
  ): Promise<{ message: string }> => {
    const response = await apiClient.delete<{ message: string }>(
      `/reviews/${reviewId}/comments/${commentId}`,
    )
    return response.data
  },
  deleteReview: async (
    reviewId: number,
  ): Promise<{ message: string; place_id?: number }> => {
    const response = await apiClient.delete<{
      message: string
      place_id?: number
    }>(`/reviews/${reviewId}`)

    // 삭제한 장소에 남아 있던 '작성 중' 임시 내용을 지운다.
    //
    // 리뷰 작성 화면은 같은 가게로 다시 들어오면 쓰던 내용을 그대로 보여준다.
    // 수정하러 되돌아온 경우엔 그게 맞지만, 리뷰를 지운 뒤라면 옛 내용이
    // 남아 있으면 안 된다. 삭제 지점이 네 군데(상세·사진상세·랭킹·목록)라
    // 각각 처리하면 빠뜨리기 쉬워서 이 한 곳에서 정리한다.
    const deletedPlaceId = response.data?.place_id
    if (deletedPlaceId != null) {
      const { storeId, resetReviewData } = useReviewStore.getState()
      if (storeId != null && String(storeId) === String(deletedPlaceId)) {
        resetReviewData()
      }
    }

    return response.data
  },
}
